import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";

const TimezoneContext = createContext();

// Timezone display names for UI
const TIMEZONE_LABELS = {
  "America/New_York": "Eastern Time",
  "America/Chicago": "Central Time",
  "America/Denver": "Mountain Time",
  "America/Phoenix": "Arizona Time",
  "America/Los_Angeles": "Pacific Time",
  "America/Anchorage": "Alaska Time",
  "Pacific/Honolulu": "Hawaii Time",
  "America/Puerto_Rico": "Atlantic Time",
  "UTC": "UTC",
  "Europe/London": "London",
  "Europe/Paris": "Central European",
  "Asia/Dubai": "Dubai",
  "Asia/Singapore": "Singapore",
  "Asia/Tokyo": "Tokyo",
  "Australia/Sydney": "Sydney",
};

export const TimezoneProvider = ({ children }) => {
  // Seed from the cached value so date math (getToday etc.) is correct from
  // the FIRST render. Without this, pages that initialize "today" on mount
  // (Jobs timeline/calendar) raced the async settings fetch: a Pacific
  // business evening (9 PM–midnight) fell on TOMORROW in the Eastern
  // default, so freshly created jobs looked missing from the Timeline.
  const [timezone, _setTimezone] = useState(() => {
    try { return localStorage.getItem("fieldz_business_tz") || "America/New_York"; }
    catch { return "America/New_York"; }
  });
  const setTimezone = (tz) => {
    if (!tz) return;
    _setTimezone(tz);
    try { localStorage.setItem("fieldz_business_tz", tz); } catch { /* private mode */ }
  };
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL 
    ? `${process.env.REACT_APP_BACKEND_URL}/api` 
    : '/api';

  // Fetch timezone from business settings
  useEffect(() => {
    const fetchTimezone = async () => {
      const token = localStorage.getItem("token") || localStorage.getItem("tech_token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/settings/business`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.timezone) {
          setTimezone(res.data.timezone);
        }
      } catch (error) {
        console.error("Failed to fetch timezone:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimezone();
  }, [API]);

  // Convert UTC date to business timezone
  const toBusinessTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return new Date(d.toLocaleString("en-US", { timeZone: timezone }));
  };

  // Format date in business timezone
  const formatDate = (date, options = {}) => {
    if (!date) return "";

    // Naive YYYY-MM-DD strings (user-entered calendar dates with no time
    // and no timezone — e.g. expenses.date, invoice.due_date) MUST be
    // rendered as the literal calendar day. Naïvely doing `new Date("2026-05-11")`
    // parses it as UTC midnight (per spec for date-only ISO strings); then
    // `toLocaleDateString({ timeZone: tz_west_of_UTC })` shifts it to the
    // previous local day, so "today" shows as "yesterday".
    //
    // Fix: build a Date at LOCAL noon from the YYYY-MM-DD components, and
    // render WITHOUT a `timeZone` override so the browser's own tz preserves
    // the calendar day. Noon makes the result robust to DST transitions.
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [y, m, dd] = date.split("-").map(Number);
      const naiveLocal = new Date(y, m - 1, dd, 12, 0, 0);
      const { timeZone: _omit, ...rest } = options;
      const opts = {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...rest,
      };
      return naiveLocal.toLocaleDateString("en-US", opts);
    }

    const d = new Date(date);
    const defaultOptions = {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
      ...options
    };
    return d.toLocaleDateString("en-US", defaultOptions);
  };

  // Format time in business timezone
  const formatTime = (date, options = {}) => {
    if (!date) return "";
    const d = new Date(date);
    const defaultOptions = {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      ...options
    };
    return d.toLocaleTimeString("en-US", defaultOptions);
  };

  // Format datetime in business timezone
  const formatDateTime = (date, options = {}) => {
    if (!date) return "";
    const d = new Date(date);
    const defaultOptions = {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...options
    };
    return d.toLocaleString("en-US", defaultOptions);
  };

  // Get start of day in business timezone (returns UTC date for API calls)
  const getStartOfDay = (date = new Date()) => {
    const d = new Date(date);
    const tzDate = new Date(d.toLocaleString("en-US", { timeZone: timezone }));
    tzDate.setHours(0, 0, 0, 0);
    // Convert back to UTC for API
    const utcDate = new Date(tzDate.toLocaleString("en-US", { timeZone: "UTC" }));
    return utcDate;
  };

  // Get end of day in business timezone (returns UTC date for API calls)
  const getEndOfDay = (date = new Date()) => {
    const d = new Date(date);
    const tzDate = new Date(d.toLocaleString("en-US", { timeZone: timezone }));
    tzDate.setHours(23, 59, 59, 999);
    // Convert back to UTC for API
    const utcDate = new Date(tzDate.toLocaleString("en-US", { timeZone: "UTC" }));
    return utcDate;
  };

  // Get today's date in business timezone
  const getToday = () => {
    const now = new Date();
    return new Date(now.toLocaleString("en-US", { timeZone: timezone }));
  };

  // Get current time in business timezone as string (HH:MM)
  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", { 
      timeZone: timezone, 
      hour: "2-digit", 
      minute: "2-digit",
      hour12: false 
    });
  };

  // Check if a date is today in business timezone
  const isToday = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const today = getToday();
    const dateInTz = new Date(d.toLocaleString("en-US", { timeZone: timezone }));
    return dateInTz.toDateString() === today.toDateString();
  };

  // Get timezone label for display
  const getTimezoneLabel = () => {
    return TIMEZONE_LABELS[timezone] || timezone;
  };

  // Get date range for a period (today, week, month, year)
  const getDateRange = (period) => {
    const today = getToday();
    let start, end;

    switch (period) {
      case "today":
        start = new Date(today);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "week":
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      case "year":
        start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  };

  // Memoized so parent re-renders (e.g. the 30s balance poll) don't hand
  // consumers new function identities — unstable getToday/getCurrentTime
  // was re-triggering form-hydration effects and wiping unsaved edits.
  const value = useMemo(() => ({
    timezone,
    setTimezone,
    loading,
    toBusinessTime,
    formatDate,
    formatTime,
    formatDateTime,
    getStartOfDay,
    getEndOfDay,
    getToday,
    getCurrentTime,
    isToday,
    getTimezoneLabel,
    getDateRange,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [timezone, loading]);

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error("useTimezone must be used within a TimezoneProvider");
  }
  return context;
};

export default TimezoneContext;
